import {
  Badge,
  BlockStack,
  Box,
  Card,
  InlineGrid,
  InlineStack,
  ProgressBar,
  Spinner,
  Text,
} from '@shopify/polaris';
import { ChartVerticalIcon } from '@shopify/polaris-icons';
import type { AuditStatus, StoreAuditRun } from '../../api/storeAudit';
import { AuditResultsList } from './AuditResultsList';
import { IconBadge } from '../shared/IconBadge';

interface Props {
  run: StoreAuditRun;
  showResults?: boolean;
}

function statusTone(status: AuditStatus): 'success' | 'attention' | 'critical' | 'info' {
  switch (status) {
    case 'completed': return 'success';
    case 'running':   return 'attention';
    case 'failed':    return 'critical';
    default:          return 'info';
  }
}

export function AuditRunCard({ run, showResults = false }: Props) {
  const isInProgress = run.status === 'pending' || run.status === 'running';

  return (
    <Card>
      <BlockStack gap="400">
        {/* Header row */}
        <InlineGrid columns={['twoThirds', 'oneThird']} gap="200">
          <InlineStack gap="300" blockAlign="center" wrap={false}>
            <IconBadge icon={ChartVerticalIcon} color={run.status === 'completed' ? 'green' : run.status === 'failed' ? 'orange' : 'indigo'} />
            <BlockStack gap="100">
              <Text as="h2" variant="headingMd">Store Audit #{run.id}</Text>
              <Text as="p" variant="bodySm" tone="subdued">
                {run.completed_at
                  ? `Completed ${new Date(run.completed_at).toLocaleString()}`
                  : run.started_at
                    ? `Started ${new Date(run.started_at).toLocaleString()}`
                    : `Queued ${new Date(run.created_at!).toLocaleString()}`}
              </Text>
            </BlockStack>
          </InlineStack>

          <div style={{ textAlign: 'right' }}>
            <Badge tone={statusTone(run.status)}>{run.status_label}</Badge>
          </div>
        </InlineGrid>

        {/* In-progress state */}
        {isInProgress && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <Spinner size="small" />
            <Text as="p" variant="bodySm" tone="subdued">
              {run.status === 'running' ? 'Running checks…' : 'Waiting to start…'}
            </Text>
          </div>
        )}

        {/* Score & stats row — completed only */}
        {run.status === 'completed' && (
          <InlineGrid columns={3} gap="400">
            <BlockStack gap="100" inlineAlign="center">
              <Text as="p" variant="headingXl" fontWeight="bold">
                {run.score_label}
              </Text>
              <Text as="p" variant="bodySm" tone="subdued">Health Score</Text>
            </BlockStack>

            <BlockStack gap="100" inlineAlign="center">
              <Text as="p" variant="headingLg">{run.passed_checks}</Text>
              <Text as="p" variant="bodySm" tone="subdued">Passed</Text>
            </BlockStack>

            <BlockStack gap="100" inlineAlign="center">
              <Text as="p" variant="headingLg">{run.failed_checks}</Text>
              <Text as="p" variant="bodySm" tone="subdued">Issues</Text>
            </BlockStack>
          </InlineGrid>
        )}

        {/* Pass-rate progress bar */}
        {run.status === 'completed' && run.total_checks > 0 && (
          <Box>
            <ProgressBar progress={run.pass_rate * 100} size="small" tone="success" />
          </Box>
        )}

        {/* Error message */}
        {run.status === 'failed' && run.error_message && (
          <Text as="p" variant="bodySm" tone="critical">{run.error_message}</Text>
        )}

        {/* Audit types badges */}
        {run.audit_types.length > 0 && (
          <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
            {run.audit_types.map((type) => (
              <Badge key={type} tone="info">{type}</Badge>
            ))}
          </div>
        )}

        {/* Expanded results list */}
        {showResults && run.results && run.results.length > 0 && (
          <AuditResultsList results={run.results} />
        )}
      </BlockStack>
    </Card>
  );
}
